import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { VLeftSidebarComponent } from './v-left-sidebar/v-left-sidebar.component';
import { VLanguageSelectorComponent } from './v-language-selector/v-language-selector.component';
import { SharedModule } from '../shared.module';
import { VCardItemComponent } from './v-card-item/v-card-item.component';
import { VFileUploadComponent } from './v-file-upload/file-upload.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { VCompareDialogComponent } from './v-compare-dialog/v-compare-dialog.component';
import { GcInfoComponent } from './v-info/v-info.component';

import { AbsPipe } from '../pipes/abs.pipe';
import { ConsolePipe } from '../pipes/console.pipe';
import { ByteSizePipe } from '../pipes/byteSize.pipe';
import { VDualMultiSelectComponent } from './v-dual-select/v-dual-select.component';
import { VOverviewComponent } from './v-overview/v-overview.component';
import { StudioSwitchSidebarComponent } from './gc-studio-switch-sidebar/gc-studio-switch-sidebar.component';
import { GcDraggableFabComponent } from './gc-draggable-fab/gc-draggable-fab.component';
import { VDatetimeComponent } from './v-datetime/v-datetime.component';
import { TimepickerModule } from './v-timepicker/timepicker.component';
import { DatepickerModule } from './gc-datepicker/datepicker.component';
import { GcTimeComponent } from './v-time/v-time.component';
import { VAddressComponent } from './v-address/v-address.component';
import { VUserComponent } from './v-user/v-user.component';
import { GcAutoCompleteModule } from './gc-autocomplete/autocomplete';
import { VContactComponent } from './v-contact/v-contact.component';
import { VDatePeriodSelectionComponent } from './v-date-period-selection/v-date-period-selection.component';
import { VDateRangeComponent } from './v-date-range/v-date-range.component';
import { VPrintPreviewComponent } from './v-print-preview/v-print-preview.component';
import { VVarDirective } from './v-var/v-var.directive';
import { GcGeneralSidebarComponent } from './v-general-sidebar/v-general-sidebar.component';
import { VTooltipModule } from './v-tooltip/v-tooltip-module';
import { VForOfDirective } from './v-for/v-for.directive';
import { VTooltipContainerComponent, VTooltipDirective } from './v-tooltip/v-tooltip.directive';
import { VKVComponent } from './v-kv/v-kv.component';
import { VQrComponent } from './v-qr/v-qr.component';
import { VQrDialogComponent } from './v-qr-dialog/v-qr-dialog.component';
import { VUserListComponent } from './v-user-list/v-user-list.component';
import { VLiComponent } from './v-li/v-li.component';
import { VOrgChartComponent } from './v-org-chart/v-org-chart.component';
import { VPersonSearchComponent } from './v-person-search/component/v-person-search.component';
import { VPersonSearchDropdownComponent } from './v-person-search/dropdown/v-person-search-dropdown.component';
import { VPersonSearchInputComponent } from './v-person-search/input-text/v-person-search-input.component';
import { VUserSelectComponent } from './v-user-select/v-user-select.component';
import { VOverviewPanelComponent } from './v-overview-panel/v-overview-panel..component';
import { VSettingsGroupComponent } from './v-settings-group/v-settings-group.component';
import { gcIsFeatureDirective } from './feature.directive';
import { gcIsPermittedDirective } from './permission.directive';
import { VSearchComponent } from './v-search/v-search.component';
import { MultiSelectModule } from 'primeng/multiselect';
import { VEventTemplateSummaryComponent } from './v-event-template-summary/v-event-template-summary.component';
import { VRestTimeEffectsComponent } from './v-rest-time-effects/v-rest-time-effects.component';
import { VAnnouncementComponent } from './v-announcement/v-announcement.component';
import { GcLayoutModule } from './v-layout/layout.module';
import { VShiftSettingsComponent } from './v-shift-settings/v-shift-settings.component';
import { GcHSLPickerComponent } from './v-hsl-picker/v-hsl-picker.component';
import { VAvatarComponent } from './v-avatar/v-avatar.component';
import { VGenericTableComponent } from './v-generic-table/v-generic-table.component';
import { VDateModule } from './v-date/v-date.module';
import { WorkflowComponent } from './workflow/workflow.component';
import { WorkflowEntitySelectionComponent } from './workflow-entity-selection/workflow-entity-selection.component';
import { WorkflowInheritanceComponent } from './workflow-inheritance/workflow-inheritance.component';
import { VisibilityComponent } from './visibility/visibility.component';
import { VisibilityInheritanceComponent } from './visibility-inheritance/visibility-inheritance.component';
import { CdkDrag, CdkDragHandle, CdkDragPreview, CdkDropList } from '@angular/cdk/drag-drop';
import { GcTabBarComponent } from './gc-tab-bar/gc-tab-bar.component';
import { CheckoutTabBarComponent } from './_purpose-built-components/checkout-tab-bar/checkout-tab-bar.component';
import { CheckoutRegisterSessionComponent } from './_purpose-built-components/checkout-register-session/checkout-register-session.component';
import { CheckoutRegisterSessionDialogComponent } from './_purpose-built-components/checkout-register-session/checkout-register-session-dialog.component';
import { MemberContractChipComponent } from './member-contract-chip/member-contract-chip.component';
import { VIconComponent } from './v-icon/v-icon.component';
import { VComponentsModule } from '../modules/wrappers';
import { GcReactiveForms, VBitButton, VDividerComponent, VFlackboxComponent } from '../modules/ngx-components';
import { RouterModule } from '@angular/router';
import { VCashRegisterBalanceComponent } from './v-cash-register-balance/v-cash-register-balance.component';
import { VControlDirective } from './v-formControl/v-formControl.directive';
import { VCurrencyDirective } from './currency.directive';
import { VTranslateDirective } from './vtranslate.directive';
import { VTranslatePipe } from '../pipes/vtranslate.pipe';
import { VErrorContentComponent } from './v-error-content/v-error-content.component';
import { VEntityComponent } from './v-entity/v-entity.component';

@NgModule({
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    SharedModule,
    FontAwesomeModule,
    ReactiveFormsModule,
    GcAutoCompleteModule,
    VTooltipModule,
    TimepickerModule,
    DatepickerModule,
    MultiSelectModule,
    GcLayoutModule,
    VDateModule,
    VVarDirective,
    VControlDirective,
    ConsolePipe,
    CdkDropList,
    CdkDragPreview,
    CdkDrag,
    CdkDragHandle,
    MemberContractChipComponent,
    GcInfoComponent,
    VKVComponent,
    VIconComponent,
    VLiComponent,
    VUserListComponent,
    VUserComponent,
    VAvatarComponent,
    VComponentsModule,
    VFlackboxComponent,
    VDividerComponent,
    VBitButton,
    GcReactiveForms,
    VCurrencyDirective,
    VTranslatePipe,
    VTranslateDirective,
    VTranslatePipe,
    VEntityComponent,
    VRestTimeEffectsComponent,
  ],
  declarations: [
    VLeftSidebarComponent,
    VLanguageSelectorComponent,
    VFileUploadComponent,
    AbsPipe,
    ByteSizePipe,
    VDatetimeComponent,
    VCardItemComponent,
    VCompareDialogComponent,
    VDualMultiSelectComponent,
    VOverviewComponent,
    VQrDialogComponent,
    StudioSwitchSidebarComponent,
    GcDraggableFabComponent,
    VDateRangeComponent,
    GcTimeComponent,
    VAddressComponent,
    VContactComponent,
    VDatePeriodSelectionComponent,
    VPrintPreviewComponent,
    GcGeneralSidebarComponent,
    VForOfDirective,
    VQrComponent,
    VOrgChartComponent,
    VPersonSearchComponent,
    VPersonSearchInputComponent,
    VPersonSearchDropdownComponent,
    VUserSelectComponent,
    VOverviewPanelComponent,
    VSettingsGroupComponent,
    gcIsFeatureDirective,
    gcIsPermittedDirective,
    VSearchComponent,
    VEventTemplateSummaryComponent,
    VAnnouncementComponent,
    VShiftSettingsComponent,
    GcHSLPickerComponent,
    VGenericTableComponent,
    WorkflowComponent,
    WorkflowEntitySelectionComponent,
    WorkflowInheritanceComponent,
    VisibilityComponent,
    VisibilityInheritanceComponent,
    GcTabBarComponent,
    CheckoutTabBarComponent,
    CheckoutRegisterSessionComponent,
    CheckoutRegisterSessionDialogComponent,
    VCashRegisterBalanceComponent, // used in ./steppers/shared-steps/checkout-devices/checkout-devices.step.haml
    VErrorContentComponent
  ],
  exports: [
    VLeftSidebarComponent,
    VLanguageSelectorComponent,
    VFileUploadComponent,
    AbsPipe,
    ByteSizePipe,
    ConsolePipe,
    VDatetimeComponent,
    VCardItemComponent,
    VCompareDialogComponent,
    GcInfoComponent,
    VDualMultiSelectComponent,
    VOverviewComponent,
    VQrDialogComponent,
    StudioSwitchSidebarComponent,
    GcDraggableFabComponent,
    VDateRangeComponent,
    GcTimeComponent,
    VAddressComponent,
    VUserComponent,
    VContactComponent,
    DatepickerModule,
    TimepickerModule,
    VDatePeriodSelectionComponent,
    VPrintPreviewComponent,
    VVarDirective,
    VControlDirective,
    GcGeneralSidebarComponent,
    VForOfDirective,
    VTooltipDirective,
    VTooltipContainerComponent,
    VKVComponent,
    VQrComponent,
    VUserListComponent,
    VLiComponent,
    VOrgChartComponent,
    VPersonSearchInputComponent,
    VPersonSearchDropdownComponent,
    VUserSelectComponent,
    VOverviewPanelComponent,
    VSettingsGroupComponent,
    gcIsFeatureDirective,
    gcIsPermittedDirective,
    VSearchComponent,
    VEventTemplateSummaryComponent,
    VRestTimeEffectsComponent,
    VAnnouncementComponent,
    VShiftSettingsComponent,
    GcHSLPickerComponent,
    VAvatarComponent,
    VDateModule,
    VGenericTableComponent,
    WorkflowComponent,
    WorkflowEntitySelectionComponent,
    WorkflowInheritanceComponent,
    VisibilityComponent,
    VisibilityInheritanceComponent,
    MemberContractChipComponent,
    GcTabBarComponent,
    CheckoutTabBarComponent,
    CheckoutRegisterSessionComponent,
    VIconComponent,
    VLiComponent,
    VFlackboxComponent,
    VDividerComponent,
    VBitButton,
    VComponentsModule,
    VCashRegisterBalanceComponent,
    VCurrencyDirective,
    VTranslateDirective,
    VTranslatePipe,
    VErrorContentComponent,
    VEntityComponent,
  ]
})
export class ComponentsModule {}
